/**
 * 学员端 H5 入口
 * -----------------------------------------------------------------------------
 * 工程基座（T-01）。页面实现在 T-18，共 11 页：
 *   引导页 / 服务台首页 / 报名表单 / 提交成功 / 缴费指引 /
 *   报名详情 / 消息中心 / 个人中心 / 帮助中心（+ 空态与驳回态）
 *
 * 安全前提：任意页面渲染前必须先确认会话，防止转发链接泄漏数据（口径 C-01）。
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { unstableSetRender } from 'antd-mobile';
import React from 'react';
import ReactDOM, { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { consumeEntryFromUrl } from './api/client';
import 'antd-mobile/es/global';
import './styles/global.css';

/**
 * 落地即记住入口码 —— **必须在 React 挂载之前同步完成**。
 * -----------------------------------------------------------------------------
 * 不放进组件 effect：`RequireSession` 一挂载就发 `/api/h5/auth/session`，
 * 那个请求要带 `x-entry` 才能命中入口级配置与归因；等 effect 跑就晚了一拍，
 * 首个请求必然漏头。这里是整个应用最早能拿到真实落地 URL 的位置。
 *
 * 地址栏的 `?entry=` 由 `App` 里的 `EntryBootstrap` 负责抹掉（那一步要用 router 的
 * `navigate` 才能让路由状态同步）。这里只负责「先存住」，顺序天然正确。
 */
consumeEntryFromUrl(window.location.search);

/**
 * React 19 兼容适配
 * -----------------------------------------------------------------------------
 * antd-mobile 5 默认兼容 React 16~18；React 19 调整了 react-dom 的导出方式，
 * 命令式组件（Toast / Dialog / Modal 等）会因拿不到 ReactDOM.render 而异常。
 * 官方提供的适配入口为 unstableSetRender（antd-mobile ^5.40.0 起支持）。
 * 该接口在 antd-mobile 下个 major 版本移除，届时可整体删除本段。
 */
unstableSetRender((node, container) => {
  const el = container as HTMLElement & { _reactRoot?: ReturnType<typeof createRoot> };
  el._reactRoot ||= createRoot(el);
  const root = el._reactRoot;
  root.render(node);
  return async () => {
    await new Promise((resolve) => setTimeout(resolve, 0));
    root.unmount();
  };
});

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
